import { Ipv4, type SiteAlert, type SiteAlertKind, type SiteDetail } from '@tabel/shared/schemas/sites';
import {
  ACTIONS,
  FIX_ACTIONS,
  STEP_BODY,
  type StepAction,
  terminalProblemText,
} from '@tabel/shared/sites/step-texts';
import { CircleAlert, KeyRound, type LucideIcon, TriangleAlert } from 'lucide-react';
import { Button, Callout, LinkButton } from '../../../components/ui';
import { ruShortDateTime } from '../../../lib/format';
import { ALERTS } from '../texts';
import { QUARANTINE_ID, REPAIR_FILE_ID } from './Repair';
import { FIX_ICON } from './StepHints';
import { type SiteCtx, useSite } from './actions';
import { STEP10_FILE_ID } from './bodies';

/*
 * «Что случилось» — открытые тревоги площадки (007 T047; spec.md FR-050, FR-051, US3 сценарий 2, US4;
 * site-onboarding-design.md раздел 6: «Тревога ведёт на страницу площадки. Там отдельный блок «Что случилось» с той же
 * диагностикой, что в шагах 3 и 4»). Каждая тревога — `Callout`: причина словами, детали сервера (время по поясу
 * площадки, адреса, числа) и действие-ссылка, где оно есть. Действие, которое исправляет причину тревоги-ошибки здесь
 * же («Разрешить адрес», «Выдать новый пароль»), — кнопка, как в подсказках шагов (`mainOf`, дизайн-система Steps).
 * Детали — как их пишет `api/src/modules/sites/alerts-eval.ts`; чего нет в деталях, того нет на экране. Тексты сервера —
 * только текстом.
 */

/** Порядок: сначала связь и приход отметок, потом источник и данные. */
const ORDER: readonly SiteAlertKind[] = [
  'pc_offline',
  'smartpss_silent',
  'extract_missed',
  'new_address',
  'auth_failures',
  'clock_skew',
  'quarantine',
  'unknown_codes',
  'schema_changed',
  'deletes',
];
/** Ошибка (`bad`) — отметки не приходят или вход под угрозой; остальное — риск (`warn`). */
const BAD: ReadonlySet<SiteAlertKind> = new Set([
  'pc_offline',
  'smartpss_silent',
  'extract_missed',
  'new_address',
  'auth_failures',
]);

/** Действие тревоги: действие шага из `step-texts` или своё — загрузка выгрузки, строки на проверке, новый пароль. */
type AlertAction =
  | { kind: 'step'; action: StepAction; changes: boolean }
  | { kind: 'upload' | 'rows' | 'reissue'; label: string };

interface AlertView {
  tone: 'bad' | 'warn';
  title: string;
  lines: readonly string[];
  actions: readonly AlertAction[];
}

const str = (d: Record<string, unknown>, k: string) =>
  typeof d[k] === 'string' && d[k] !== '' ? d[k] : null;
const num = (d: Record<string, unknown>, k: string) =>
  typeof d[k] === 'number' && Number.isFinite(d[k]) ? d[k] : null;
const goto = (step: number): AlertAction => ({
  kind: 'step',
  action: ACTIONS.gotoStep(step),
  changes: false,
});

/** Вид тревоги по виду и деталям сервера. */
function alertView(a: SiteAlert, d: SiteDetail): AlertView {
  const tz = d.site.tzOffset;
  const at = (v: string | null) => (v && Number.isFinite(Date.parse(v)) ? ruShortDateTime(v, tz) : null);
  const det = a.details;
  const K = ALERTS.kinds;
  const tone = BAD.has(a.kind) ? 'bad' : 'warn';
  const lines: string[] = [];
  const actions: AlertAction[] = [];
  let title: string;
  switch (a.kind) {
    case 'pc_offline': {
      title = K.pc_offline.title;
      const last = at(str(det, 'lastSignalAt'));
      if (last) lines.push(K.pc_offline.last(last));
      lines.push(K.pc_offline.text);
      actions.push(goto(3));
      break;
    }
    case 'smartpss_silent': {
      title = K.smartpss_silent.title;
      const last = at(str(det, 'lastRowAt'));
      if (last) lines.push(K.smartpss_silent.last(last));
      lines.push(K.smartpss_silent.text);
      actions.push(goto(4));
      break;
    }
    case 'extract_missed': {
      title = K.extract_missed.title;
      // С датой, как у других тревог: назавтра голое «08:00» прочтут как сегодняшнее.
      const when = at(str(det, 'at'));
      if (when) lines.push(K.extract_missed.at(when));
      lines.push(K.extract_missed.text);
      actions.push(goto(7), { kind: 'upload', label: ALERTS.upload });
      break;
    }
    case 'new_address': {
      title = K.new_address.title;
      const from = str(det, 'from');
      const allowed = str(det, 'allowed');
      if (from) lines.push(allowed ? K.new_address.from(from, allowed) : K.new_address.fromOnly(from));
      lines.push(K.new_address.text);
      // Разрешить можно только адрес IPv4 из отказов: сервер проверит его ещё раз (`ADDRESS_NOT_SEEN`).
      if (from && Ipv4.safeParse(from).success)
        actions.push({ kind: 'step', action: ACTIONS.allowAddress(from), changes: true });
      break;
    }
    case 'auth_failures': {
      title = K.auth_failures.title;
      const n = num(det, 'lastHour');
      if (n !== null) lines.push(K.auth_failures.count(n));
      lines.push(K.auth_failures.text);
      if (d.steps.find((s) => s.step === 1)?.status === 'ok')
        actions.push({ kind: 'reissue', label: STEP_BODY[4].reissue });
      break;
    }
    case 'clock_skew': {
      title = K.clock_skew.title;
      const list = Array.isArray(det.terminals) ? det.terminals : [];
      for (const t of list) {
        if (!t || typeof t !== 'object') continue;
        const o = t as Record<string, unknown>;
        const ip = str(o, 'ip');
        const minutes = num(o, 'minutes');
        if (ip && minutes !== null)
          lines.push(`${ip}. ${terminalProblemText('CLOCK_SKEW', { skewMinutes: minutes })}`);
      }
      actions.push(goto(5));
      break;
    }
    case 'quarantine': {
      title = K.quarantine.title;
      const n = num(det, 'rows');
      if (n !== null) lines.push(K.quarantine.rows(n));
      lines.push(K.quarantine.text);
      actions.push({ kind: 'rows', label: ALERTS.showRows });
      break;
    }
    case 'unknown_codes':
      title = K.unknown_codes.title;
      lines.push(K.unknown_codes.text);
      actions.push({ kind: 'rows', label: ALERTS.showRows });
      break;
    case 'schema_changed':
      title = K.schema_changed.title;
      lines.push(K.schema_changed.text);
      actions.push({ kind: 'rows', label: ALERTS.showRows });
      break;
    case 'deletes': {
      title = K.deletes.title;
      const n = num(det, 'today');
      if (n !== null) lines.push(K.deletes.today(n));
      lines.push(K.deletes.text);
      break;
    }
  }
  return { tone, title, lines, actions };
}

const ICON: Readonly<Record<AlertView['tone'], LucideIcon>> = { bad: CircleAlert, warn: TriangleAlert };

/**
 * Главное действие тревоги-ошибки — первое, что исправляет причину здесь же: исправляющее действие шага (`FIX_ACTIONS`)
 * или «Выдать новый пароль». Переход к шагу, строки в «Ремонте» и «Загрузить выгрузку» — ссылки: выгрузка не
 * подтверждена — ещё не сбой, сначала проверяют SmartPSS. Нет — `null`.
 */
function mainOf(v: AlertView): AlertAction | null {
  if (v.tone !== 'bad') return null;
  const fixes = (x: AlertAction) =>
    x.kind === 'reissue' || (x.kind === 'step' && FIX_ACTIONS.includes(x.action.kind));
  return v.actions.find(fixes) ?? null;
}

export function SiteAlerts() {
  const ctx = useSite();
  const { detail } = ctx;
  if (detail.alerts.length === 0) return null;
  const list = [...detail.alerts].sort((a, b) => ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind));
  return (
    <section aria-labelledby="site-alerts">
      <h2 id="site-alerts" className="t-set-sub bad">
        {ALERTS.title}
      </h2>
      <div className="mt-3 flex flex-col gap-2">
        {list.map((a) => {
          const v = alertView(a, detail);
          const main = mainOf(v);
          return (
            <div key={a.id} data-testid="site-alert" data-kind={a.kind} data-tone={v.tone}>
              <Callout tone={v.tone} icon={ICON[v.tone]}>
                <p>
                  <b className="font-semibold">{v.title}</b>
                </p>
                {v.lines.map((line) => (
                  <p key={line}>{line}</p>
                ))}
                <p className="mt-0.5 text-[12px] text-ink-3 tabular-nums">
                  {ALERTS.since(ruShortDateTime(a.openedAt, detail.site.tzOffset))}
                </p>
                {v.actions.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
                    {v.actions.map((x) => (
                      <AlertActionLink key={label(x)} action={x} main={x === main} ctx={ctx} />
                    ))}
                  </div>
                )}
              </Callout>
            </div>
          );
        })}
      </div>
    </section>
  );
}

const label = (x: AlertAction) => (x.kind === 'step' ? x.action.label : x.label);

function AlertActionLink({ action: x, main, ctx }: { action: AlertAction; main: boolean; ctx: SiteCtx }) {
  const { detail, act, busy, setDialog } = ctx;
  const off = detail.site.state === 'disabled' || busy !== null;
  const changes = x.kind === 'step' ? x.changes : x.kind !== 'rows';
  const onClick = () => {
    switch (x.kind) {
      case 'step':
        // «Разрешить адрес» и «Открыть шаг N» — как в подсказках шагов (T041).
        act.hint(x.action, x.action.kind === 'gotoStep' ? x.action.step : 4);
        break;
      case 'upload':
        openUpload(act);
        break;
      case 'rows': {
        const title = document.getElementById(QUARANTINE_ID);
        title?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
        title?.focus();
        break;
      }
      case 'reissue':
        setDialog({ kind: 'reissue', what: 'password' });
        break;
    }
  };
  if (main)
    return (
      <Button
        icon={x.kind === 'step' ? FIX_ICON[x.action.kind] : KeyRound}
        disabled={changes && off}
        onClick={onClick}
      >
        {label(x)}
      </Button>
    );
  return (
    <LinkButton disabled={changes && off} onClick={onClick}>
      {label(x)}
    </LinkButton>
  );
}

/**
 * «Загрузить выгрузку»: выбор файла выгрузки записей. После запуска — в «Ремонте», до запуска — в шаге 10 (шаг
 * раскрывается). Выбор файла открывает браузер; вызов — в обработчике нажатия.
 */
function openUpload(act: SiteCtx['act']) {
  const repair = document.getElementById(REPAIR_FILE_ID);
  if (repair instanceof HTMLInputElement) {
    repair.closest('section')?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    repair.click();
    return;
  }
  act.hint(ACTIONS.gotoStep(10), 10);
  const step10 = document.getElementById(STEP10_FILE_ID);
  if (step10 instanceof HTMLInputElement && !step10.disabled) step10.click();
}
